import type { ComponentProps } from "react"

import { useTranslations } from "next-intl"

import { QUINCENAS } from "@/components/marketplace/bnpl/price-tiers"
import { moneyShort } from "@/components/marketplace/format"
import { SectionHeader } from "@/components/marketplace/layout/section-header"
import { ArrowIcon, Reel, Text } from "@/components/ui"
import { Link } from "@/i18n/navigation"

interface GiftTier {
  id: string
  label: string
  href?: string
  minPrice?: number
  maxPrice?: number
  serveId?: string
}

// A price bucket is a shortcut, so it looks like one. As cards these were four
// tall white rectangles carrying a heading, a rule, an arrow disc and the words
// "sin intereses" printed four times — a filter row dressed as a section.
// Urban Outfitters runs its "$25 & Under" buckets as a single line of compact
// tickets under the editorial tiles, which is the honest weight for a
// destination that is really just a query. The claim is stated once, above.
const tierClass =
  "group press border-line hover:border-brand hover:bg-brand-subtle duration-moderate ease-productive flex shrink-0 snap-start items-center gap-250 rounded-full border bg-surface py-250 pr-350 pl-400 transition-colors"

interface GiftsByPriceProps {
  tiers: GiftTier[]
  title: string
  accent?: string
  headerSize?: ComponentProps<typeof SectionHeader>["size"]
  numeral?: string
}

export const GiftsByPrice = ({ tiers, title, accent, headerSize, numeral }: GiftsByPriceProps) => {
  const t = useTranslations("marketplace.discovery")
  return (
    <div className="px-gut">
      <SectionHeader title={title} accent={accent} eyebrow={t("noInterest")} size={headerSize} numeral={numeral} className="mb-500" />
      <Reel gap="250" snap className="pb-100">
        {tiers.map((tier, index) => {
          const bound = tier.maxPrice ?? tier.minPrice
          const quincena =
            bound === undefined ? null : t(tier.maxPrice === undefined ? "quincenaFrom" : "quincenaUpTo", { count: QUINCENAS, amount: moneyShort(bound / QUINCENAS) })
          const content = (
            <>
              <Text as="span" variant="body" family="display" tone="default" numeric className="@3xl:text-body-lg font-extrabold tracking-tight">
                {tier.label}
              </Text>
              {quincena && (
                <>
                  <span aria-hidden className="bg-line size-1 shrink-0 rounded-full" />
                  <Text as="span" variant="bodySm" family="display" tone="brand" numeric className="font-bold">
                    {quincena}
                  </Text>
                </>
              )}
              <ArrowIcon
                size={15}
                aria-hidden
                className="text-fg-faint group-hover:text-fg-brand duration-moderate ease-productive shrink-0 transition-transform motion-safe:group-hover:translate-x-0.5"
              />
            </>
          )
          const style = { animationDelay: `${Math.min(index, 8) * 70}ms` }
          return tier.href ? (
            <Link
              key={tier.id}
              href={tier.href}
              data-nelo-serve={tier.serveId}
              style={style}
              className={`${tierClass} motion-safe:animate-rise-in focus-visible:ring-brand focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none`}
            >
              {content}
            </Link>
          ) : (
            <div key={tier.id} data-nelo-serve={tier.serveId} style={style} className={`${tierClass} motion-safe:animate-rise-in`}>
              {content}
            </div>
          )
        })}
      </Reel>
    </div>
  )
}
